// The match shell's local UI state: debug flag, hover/selection, the camera
// go-to request and the modal/drawer flags. Extracted from App.tsx
// (ROADMAP 7.14); the hook order is unchanged.

import type { SceneNode } from '@aod/protocol';
import { useCallback, useRef, useState } from 'react';
import type { MobileHudPanel } from '../components/MobileHudToggles.js';
import { readPersistentFlag, writePersistentFlag } from '../lib/persistentFlag.js';
import { scalePoint } from '../render/boardScale.js';
import type { CameraFocusRequest, Vec3 } from '../render/camera/flight.js';
import type { Selection } from '../testBridge/useTestBridge.js';

export function useShellUiState() {
  const [showDebug, setShowDebugRaw] = useState(() =>
    readPersistentFlag('aod:showDebug', import.meta.env.DEV),
  );
  const setShowDebug = useCallback((next: boolean | ((v: boolean) => boolean)) => {
    setShowDebugRaw((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writePersistentFlag('aod:showDebug', value);
      return value;
    });
  }, []);
  const [hoveredTile, setHoveredTile] = useState<SceneNode | null>(null);
  const [hoveredUnit, setHoveredUnit] = useState<SceneNode | null>(null);
  const [selection, setSelection] = useState<Selection>(null);
  // Camera go-to requests for the Scene (one glide each, keyed on seq).
  const [cameraRequest, setCameraRequest] = useState<CameraFocusRequest | null>(null);
  const cameraSeq = useRef(0);
  const flyCameraTo = useCallback((position: Vec3, when: CameraFocusRequest['when'] = 'always') => {
    cameraSeq.current += 1;
    // Callers pass board-space coords (snapshot transforms / axialToWorld);
    // the rendered board is scaled (Large Board setting). boardPosition keeps
    // the raw point for consumers that compare against snapshot transforms.
    setCameraRequest({
      position: scalePoint(position),
      boardPosition: position,
      seq: cameraSeq.current,
      when,
    });
  }, []);
  // Where the player explicitly sent the camera (minimap / "Go to"): the
  // audio bed samples the biome there. Selection glides don't count.
  const cameraTarget = cameraRequest?.when === 'always' ? cameraRequest.boardPosition : null;
  const [showPostGameSummary, setShowPostGameSummary] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [coachmarkMenuOpen, setCoachmarkMenuOpen] = useState(false);
  const [combatModalOpen, setCombatModalOpen] = useState(true);
  // Below md the side rails are drawers; at most one is open at a time.
  const [mobilePanel, setMobilePanel] = useState<MobileHudPanel>('none');
  const [combatSelectedIndex, setCombatSelectedIndex] = useState(0);

  return {
    showDebug,
    setShowDebug,
    hoveredTile,
    setHoveredTile,
    hoveredUnit,
    setHoveredUnit,
    selection,
    setSelection,
    cameraRequest,
    setCameraRequest,
    flyCameraTo,
    cameraTarget,
    showPostGameSummary,
    setShowPostGameSummary,
    settingsOpen,
    setSettingsOpen,
    helpOpen,
    setHelpOpen,
    coachmarkMenuOpen,
    setCoachmarkMenuOpen,
    combatModalOpen,
    setCombatModalOpen,
    mobilePanel,
    setMobilePanel,
    combatSelectedIndex,
    setCombatSelectedIndex,
  };
}
